import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import './pieChart.scss';
import GraphModal from '../graphModal/GraphModal';
import Skeleton from "@mui/material/Skeleton";
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterBadge from "../common/FilterBadge";

const CustomPieChart = ({ data, colors, title, loading = false, appliedFilters = [] }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const hasFilters = appliedFilters.length > 0 && !loading;

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.98;
        const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
        const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

        const sin = Math.sin(-midAngle * (Math.PI / 180));
        const cos = Math.cos(-midAngle * (Math.PI / 180));

        // Adjust label positioning based on the quadrant
        let textAnchor = 'middle';
        let dy = 0;
        if (cos < 0) {
            textAnchor = 'start';
        } else {
            textAnchor = 'end';
        }
        if (sin < 0) {
            dy = 16;
        } else {
            dy = -16;
        }

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor={textAnchor}
                dominantBaseline="central"
                dy={dy}
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    if (loading) {
        return (
            <div className="pieChartContainer">
                {title && <div className="chartTitle">{title}</div>}
                <Skeleton variant="rectangular" width="100%" height={400} />
            </div>
        );
    }

    return (
        <div
            className="pieChartContainer"
            onMouseEnter={() => setShowFilters(true)}
            onMouseLeave={() => setShowFilters(false)}
        >
            <div className="chart-header">
                <h2 className="chartTitle">{title}</h2>
                {hasFilters && (
                    <div className="filter-indicator">
                        <FilterListIcon fontSize="small" />
                    </div>
                )}
            </div>

            {hasFilters && showFilters && (
                <div className="filter-tooltip top-left">
                    <div className="filter-header">Applied Filters:</div>
                    <div className="filter-badges">
                        {appliedFilters.map((filter, index) => (
                            <FilterBadge
                                key={index}
                                label={filter.label}
                                value={filter.value}
                            />
                        ))}
                    </div>
                </div>
            )}

            {loading ? (
                <Skeleton variant="rectangular" width="100%" height={400} />
            ) : (
                <>
                    <div onClick={handleOpenModal} className="chart-wrapper">
                        <ResponsiveContainer width="100%" height={400}>
                            <RechartsPieChart>
                                <Pie
                                    data={data}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={true}
                                    label={renderCustomizedLabel}
                                    outerRadius={130}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {data.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => [`${value} students`, 'Count']} />
                                <Legend align="center" verticalAlign="bottom" />
                            </RechartsPieChart>
                        </ResponsiveContainer>
                    </div>

                    <GraphModal isOpen={isModalOpen} onClose={handleCloseModal} title={title}>
                        <ResponsiveContainer width="100%" height={500}>
                            <RechartsPieChart>
                                <Pie
                                    data={data}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={true}
                                    label={renderCustomizedLabel}
                                    outerRadius={200}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {data.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => [`${value} students`, 'Count']} />
                                <Legend align="center" verticalAlign="bottom" />
                            </RechartsPieChart>
                        </ResponsiveContainer>
                    </GraphModal>
                </>
            )}
        </div>
    );
};

CustomPieChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ).isRequired,
    colors: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    loading: PropTypes.bool
};

CustomPieChart.defaultProps = {
    colors: ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF193D'],
    data: [],
    loading: false
};

export default CustomPieChart;